import { useState } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  Download,
  CheckCircle2,
  Smartphone,
  Monitor,
  Chrome,
  Globe,
} from 'lucide-react';
import { useKioskInstall } from '../hooks/useKioskInstall';

/**
 * Guided PWA installation flow for the kiosk admin panel.
 * Detects install capability and provides per-browser instructions.
 */
export default function KioskAdminInstall() {
  const { canInstall, isInstalled, promptInstall } = useKioskInstall();
  const [installing, setInstalling] = useState(false);

  const handleInstall = async () => {
    setInstalling(true);
    await promptInstall();
    setInstalling(false);
  };

  // Detect browser for manual instructions
  const ua = navigator.userAgent;
  const isChrome = /Chrome/.test(ua) && !/Edge/.test(ua);
  const isEdge = /Edg/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
  const isFirefox = /Firefox/.test(ua);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-800">Install Kiosk App</h2>
        <p className="text-sm text-slate-500 mt-1">
          Install this kiosk as a standalone app for the best offline experience
        </p>
      </div>

      {/* Already installed */}
      {isInstalled && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 flex items-center gap-4"
        >
          <CheckCircle2 className="w-10 h-10 text-emerald-600 flex-shrink-0" />
          <div>
            <h3 className="text-base font-semibold text-emerald-800">App is installed</h3>
            <p className="text-sm text-emerald-600 mt-1">
              This kiosk is running as a standalone app with full offline support.
            </p>
          </div>
        </motion.div>
      )}

      {/* Can install via prompt */}
      {!isInstalled && canInstall && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-slate-200 bg-white p-6 space-y-4"
        >
          <div className="flex items-center gap-3">
            <Download className="w-8 h-8 text-slate-700" />
            <div>
              <h3 className="text-base font-semibold text-slate-800">Ready to Install</h3>
              <p className="text-sm text-slate-500">
                Install the kiosk app to this device for offline access
              </p>
            </div>
          </div>

          <Button
            onClick={handleInstall}
            disabled={installing}
            className="w-full h-14 text-lg font-semibold rounded-xl"
          >
            {installing ? 'Installing...' : 'Install Kiosk App'}
          </Button>

          <p className="text-xs text-slate-400 text-center">
            The app will be added to your home screen and work without internet
          </p>
        </motion.div>
      )}

      {/* Manual instructions */}
      {!isInstalled && !canInstall && (
        <div className="space-y-4">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm text-amber-700">
              Your browser doesn't support automatic installation. Follow the manual steps below.
            </p>
          </div>

          {/* Chrome instructions */}
          {isChrome && (
            <InstructionCard
              icon={<Chrome className="w-6 h-6" />}
              title="Google Chrome"
              steps={[
                'Tap the menu icon (three dots) in the top-right corner',
                'Select "Install app" or "Add to Home screen"',
                'Tap "Install" to confirm',
                'The kiosk app will appear on your home screen',
              ]}
            />
          )}

          {/* Edge instructions */}
          {isEdge && (
            <InstructionCard
              icon={<Globe className="w-6 h-6" />}
              title="Microsoft Edge"
              steps={[
                'Tap the menu icon (three dots) in the top-right corner',
                'Select "Apps" then "Install this site as an app"',
                'Tap "Install" to confirm',
                'The kiosk app will appear on your home screen',
              ]}
            />
          )}

          {/* Safari instructions */}
          {isSafari && (
            <InstructionCard
              icon={<Smartphone className="w-6 h-6" />}
              title="Safari (iOS)"
              steps={[
                'Tap the Share button (square with arrow) at the bottom of the screen',
                'Scroll down and tap "Add to Home Screen"',
                'Enter a name for the app (e.g., "Kiosk")',
                'Tap "Add" to confirm',
              ]}
            />
          )}

          {/* Firefox */}
          {isFirefox && (
            <InstructionCard
              icon={<Globe className="w-6 h-6" />}
              title="Firefox"
              steps={[
                'Firefox has limited PWA support',
                'For the best experience, use Google Chrome or Microsoft Edge',
                'Open this URL in Chrome and install from there',
              ]}
            />
          )}

          {/* Generic fallback */}
          {!isChrome && !isEdge && !isSafari && !isFirefox && (
            <InstructionCard
              icon={<Monitor className="w-6 h-6" />}
              title="Install Instructions"
              steps={[
                'Look for an "Install" or "Add to Home Screen" option in your browser menu',
                'For the best experience, use Google Chrome or Microsoft Edge',
                'After installing, open the app from your home screen',
              ]}
            />
          )}
        </div>
      )}

      {/* Benefits list */}
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Benefits of installing</h3>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            Works completely offline — no internet required
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            Full-screen experience without browser UI
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            Launches directly from home screen
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            Data stored securely on this device
          </li>
        </ul>
      </div>
    </div>
  );
}

function InstructionCard({ icon, title, steps }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
      <div className="flex items-center gap-2 text-slate-700">
        {icon}
        <h3 className="text-base font-semibold">{title}</h3>
      </div>
      <ol className="space-y-2 ml-1">
        {steps.map((step, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-xs font-semibold flex items-center justify-center mt-0.5">
              {i + 1}
            </span>
            {step}
          </li>
        ))}
      </ol>
    </div>
  );
}
